import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, Loader2, RotateCcw } from "lucide-react";

export const GerarDossierFinal = ({ pid, ref_, ready = false, provisional = false, block = false, blocker = null, onChange }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [suggested, setSuggested] = useState("");
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const saveCode = async () => {
    try { await api.put(`/processes/${pid}`, { distrate_code: code.trim() }); toast.success("Código de cancelamento guardado"); onChange?.(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const abrir = async () => {
    try {
      const { data } = await api.get(`/processes/${pid}/dossier/notes`);
      setNotes(data.notes || "");
      setSuggested(data.suggested || "");
      setOpen(true);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const gerar = async () => {
    const win = window.open("", "_blank");
    setBusy(true);
    try {
      await api.put(`/processes/${pid}/dossier/notes`, { notes });
      const res = await api.get(`/processes/${pid}/dossier`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (win) win.location.href = url;
      else { const a = document.createElement("a"); a.href = url; a.download = `Dossier_${ref_}.pdf`; a.click(); }
      const prov = res.headers["x-provisional"] === "1";
      const val = res.headers["x-validated"] || "";
      if (prov) toast.warning(`Dossier provisório gerado · ${val} documentos verificados`);
      else toast.success(`Dossier final gerado · ${val} documentos verificados`);
      setOpen(false);
    } catch (e) {
      if (win) win.close();
      toast.error("Não foi possível gerar o Dossier final");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={abrir} data-testid="gerar-dossier-btn" data-final={provisional ? "0" : "1"} data-ready={ready ? "1" : "0"}
        title={provisional ? "Há documentos em falta ou com problema: o PDF sai com a marca ‘Provisório’ em todas as páginas" : "Todos os documentos OK"}
        className={`inline-flex items-center justify-center gap-2 text-[15px] px-5 py-2.5 rounded-lg font-semibold transition-colors ${block ? "w-full" : ""} ${ready ? "bg-ec-gold text-ec-green hover:brightness-105 ring-4 ring-ec-gold/30 shadow-[0_6px_20px_rgba(201,168,106,0.55)]" : "border border-slate-200 bg-white text-ec-green hover:bg-slate-50"}`}>
        <BookOpen size={15} /> {ready ? "Gerar dossier final" : "Pré-visualizar dossier"}
      </button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="sm:max-w-2xl" data-testid="dossier-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-ec-green">Notas para quem prepara a escritura</DialogTitle>
            <DialogDescription>
              Sugeridas a partir dos avisos do processo. Edite à vontade: uma nota por linha. Ficam guardadas para a próxima vez.
            </DialogDescription>
          </DialogHeader>
          {blocker && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="dossier-final-blocker">
              {blocker} O PDF sai como provisório.
              <div className="flex gap-2 mt-2">
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Código de cancelamento eletrónico"
                  className="flex-1 h-9 rounded-md border border-amber-200 bg-white px-3 text-sm" data-testid="distrate-code-input" />
                <button onClick={saveCode} disabled={!code.trim()} className="px-3 rounded-md bg-ec-green text-white text-sm font-semibold disabled:opacity-40" data-testid="distrate-code-save">Guardar</button>
              </div>
            </div>
          )}
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={10}
            className="text-sm" data-testid="dossier-notes-textarea" placeholder="Sem notas." />
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-between">
            <button onClick={() => setNotes(suggested)} disabled={busy} data-testid="dossier-notes-reset"
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-ec-green transition-colors">
              <RotateCcw size={14} /> Repor sugestões
            </button>
            <button onClick={gerar} disabled={busy} data-testid="dossier-generate-confirm"
              className="inline-flex items-center justify-center gap-2 bg-ec-green text-white px-4 py-2 rounded-lg hover:bg-ec-greenDark transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <BookOpen size={14} />} Guardar notas e gerar PDF
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
